/**
 * The intent-resolve dialog (Phase 2, Intents screen's row action). An open intent means the sweep
 * called (or was about to call) Zoom and then crashed before the completion write — the SWEEP genuinely
 * does not know what happened, so a human has to look at Zoom directly and say so. Two steps on
 * purpose: compose (note + pick an outcome) then a separate confirm step before the mutation actually
 * fires, because this overrides the sweep's own account of events and the consequence (retry vs. don't)
 * is real. The server's consequence string is shown VERBATIM in the success toast, never paraphrased.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, Modal, useNotifier } from "@/components/ui";
import { INTENT_ACTION_LABEL_KEY, validateResolveNote } from "@/features/pool/intentValidation";
import { useResolveIntent } from "@/features/pool/hooks";
import { formatInstant } from "@/lib/format";
import type { PoolIntent } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

type Phase = { kind: "compose" } | { kind: "confirm"; success: boolean };

export function ResolveIntentDialog({
  intent,
  onClose,
}: {
  intent: PoolIntent;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const resolveMut = useResolveIntent();

  const [phase, setPhase] = useState<Phase>({ kind: "compose" });
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const email = intent.target_email ?? "—";

  function chooseOutcome(success: boolean) {
    const key = validateResolveNote(note);
    if (key) {
      setNoteError(t(key));
      return;
    }
    setNoteError(null);
    setSubmitError(null);
    setPhase({ kind: "confirm", success });
  }

  function confirmSubmit() {
    if (phase.kind !== "confirm") return;
    setSubmitError(null);
    resolveMut.mutate(
      { auditId: intent.id, body: { success: phase.success, note: note.trim() } },
      {
        onSuccess: (res) => {
          notify({
            tone: "ok",
            title: t("pool.intents.resolveSuccessToastTitle"),
            // Verbatim — this is the server's account of the consequence, not our own paraphrase.
            body: res.consequence,
          });
          onClose();
        },
        onError: (e) => setSubmitError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  const isConfirm = phase.kind === "confirm";

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.intents.resolveTitle", { email })}
      size="md"
      footer={
        isConfirm ? (
          <>
            <Button variant="secondary" onClick={() => setPhase({ kind: "compose" })}>
              {t("action.cancel")}
            </Button>
            <Button
              variant={phase.success ? "primary" : "danger"}
              loading={resolveMut.isPending}
              onClick={confirmSubmit}
            >
              {t("action.confirm")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="danger" onClick={() => chooseOutcome(false)}>
              {t("pool.intents.failedButton")}
            </Button>
            <Button variant="primary" onClick={() => chooseOutcome(true)}>
              {t("pool.intents.successButton")}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
          <p className="text-(length:--text-sm) text-(--text)">
            {t("pool.intents.resolveExplanation")}
          </p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-(length:--text-xs) text-(--text-muted)">
            <dt className="font-medium text-(--text-subtle)">{t("pool.intents.colAction")}</dt>
            <dd>{t(INTENT_ACTION_LABEL_KEY[intent.action])}</dd>
            <dt className="font-medium text-(--text-subtle)">{t("pool.intents.colTrigger")}</dt>
            <dd>{intent.trigger}</dd>
            <dt className="font-medium text-(--text-subtle)">{t("pool.intents.colTs")}</dt>
            <dd>{formatInstant(intent.ts)}</dd>
          </dl>
        </div>

        {!isConfirm && (
          <label className="flex flex-col gap-1.5">
            <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
              {t("pool.intents.noteLabel")}
            </span>
            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setNoteError(null);
              }}
              placeholder={t("pool.intents.notePlaceholder")}
              rows={3}
              className="w-full rounded-(--radius-sm) border border-(--border-input) bg-(--surface) px-3 py-2 text-(length:--text-sm) text-(--text) placeholder:text-(--text-placeholder) transition-[border-color,box-shadow] duration-(--dur-fast) ease-(--ease-out) hover:border-(--border-strong) focus:border-(--brand) focus:shadow-[0_0_0_3px_var(--ring)] focus-visible:outline-none"
            />
            {noteError && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {noteError}
              </p>
            )}
          </label>
        )}

        {isConfirm && (
          <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) p-3">
            <p className="text-(length:--text-sm) font-medium text-(--text)">
              {t(
                phase.success
                  ? "pool.intents.confirmSuccessTitle"
                  : "pool.intents.confirmFailedTitle",
              )}
            </p>
            <p className="text-(length:--text-xs) text-(--text-muted)">
              {t(
                phase.success
                  ? "pool.intents.confirmSuccessBody"
                  : "pool.intents.confirmFailedBody",
                { email },
              )}
            </p>
            <p className="text-(length:--text-xs) italic text-(--text-subtle)">“{note.trim()}”</p>
          </div>
        )}

        {submitError && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
